import { useEffect, useCallback, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
  type CarouselApi,
} from '@/components/ui/carousel'
import { Button } from '@/components/ui/button'
import { useLineItemImages } from '@/hooks/useImages'
import { useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/lib/queryKeys'
import { Loader2, Pencil } from 'lucide-react'
import { ReorderableImageGallery } from './ReorderableImageGallery'

interface ImageCarouselModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  planId: string
  versionId: string
  lineId: string
}

export function ImageCarouselModal({
  open,
  onOpenChange,
  planId,
  versionId,
  lineId,
}: ImageCarouselModalProps) {
  const queryClient = useQueryClient()
  const { data: images, isLoading } = useLineItemImages(
    open ? planId : undefined,
    open ? versionId : undefined,
    open ? lineId : undefined
  )

  const [api, setApi] = useState<CarouselApi>()
  const [current, setCurrent] = useState(0)
  const [count, setCount] = useState(0)
  const [isEditMode, setIsEditMode] = useState(false)

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      queryClient.invalidateQueries({
        queryKey: queryKeys.dagTable.rowsPrefix(planId, versionId),
      })
    }
    onOpenChange(isOpen)
  }

  useEffect(() => {
    if (!open) {
      setIsEditMode(false)
    }
  }, [open])

  useEffect(() => {
    if (!api) return

    setCount(api.scrollSnapList().length)
    setCurrent(api.selectedScrollSnap())

    api.on('select', () => {
      setCurrent(api.selectedScrollSnap())
    })
  }, [api])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (isEditMode) return
      if (e.key === 'ArrowLeft') api?.scrollPrev()
      if (e.key === 'ArrowRight') api?.scrollNext()
    },
    [api, isEditMode]
  )

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="sm:max-w-2xl"
        onKeyDown={handleKeyDown}
        onPointerDownOutside={isEditMode ? e => e.preventDefault() : undefined}
        onInteractOutside={isEditMode ? e => e.preventDefault() : undefined}
      >
        <DialogHeader>
          <DialogTitle>Line Item Images</DialogTitle>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : !images || images.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <p className="text-sm text-muted-foreground">
              No images for this line item
            </p>
          </div>
        ) : isEditMode ? (
          <div className="space-y-4">
            <ReorderableImageGallery
              planId={planId}
              versionId={versionId}
              lineId={lineId}
            />
            <div className="flex justify-end">
              <Button
                variant="outline"
                size="sm"
                className="cursor-pointer"
                onClick={() => setIsEditMode(false)}
              >
                Done
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <Carousel setApi={setApi} className="w-full">
              <CarouselContent>
                {images.map(image => (
                  <CarouselItem key={image.id}>
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center justify-center w-full max-h-[500px]">
                        <img
                          src={image.download_url || image.thumbnail_url || ''}
                          alt={image.name || image.original_filename || 'Image'}
                          className="max-h-[500px] w-auto object-contain rounded-md"
                        />
                      </div>
                      <p className="text-sm font-medium text-foreground text-center">
                        {image.name || image.original_filename}
                      </p>
                    </div>
                  </CarouselItem>
                ))}
              </CarouselContent>
              {images.length > 1 && (
                <>
                  <CarouselPrevious />
                  <CarouselNext />
                </>
              )}
            </Carousel>

            <div className="flex items-center justify-center gap-3">
              {count > 1 && (
                <p className="text-xs text-muted-foreground">
                  {current + 1} of {count}
                </p>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="h-7 cursor-pointer text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setIsEditMode(!isEditMode)}
              >
                <Pencil className="mr-1 h-3 w-3" />
                Edit
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}
